import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, MapPin } from "lucide-react";
import { imgUrl, fmt } from "@/lib/api";
import { BookingDialog } from "@/components/BookingDialog";

export const ListingDetail = ({ listing, type, open, onClose }) => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  if (!listing) return null;
  const images = (listing.images || []).map(imgUrl);
  const tiers = listing.price_tiers || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid={`listing-detail-${listing.id}`}
        className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-3xl max-h-[92vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">{listing.title}</DialogTitle>
        <DialogDescription className="sr-only">Listing details and photo gallery</DialogDescription>
        <div className="relative">
          <button onClick={() => setLightbox(true)} data-testid="listing-main-photo" className="w-full">
            <img src={images[active]} alt={listing.title} className="w-full aspect-[16/10] object-cover" />
          </button>
          <span className="absolute bottom-3 right-3 text-[10px] tracking-widest uppercase bg-black/60 px-3 py-1.5 text-zinc-300">
            Click to expand
          </span>
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 px-6 pt-4 overflow-x-auto">
            {images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`listing-thumb-${i}`}
                className={`shrink-0 border-2 transition-colors ${i === active ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={img} alt="" className="w-20 h-14 object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-4xl text-white">{listing.title}</h2>
              {listing.location && (
                <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {listing.location}
                </p>
              )}
            </div>
            {type === "car" && listing.price > 0 && (
              <span className="text-brand font-semibold whitespace-nowrap">{fmt(listing.price)}/hr</span>
            )}
          </div>
          {type === "house" && Object.keys(tiers).length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-5" data-testid="house-tiers">
              {["4", "6", "8"].map((h) => (
                <div key={h} className="border border-white/10 p-4 text-center">
                  <p className="text-[10px] tracking-widest uppercase text-zinc-500">{h} hours</p>
                  <p className="font-display text-2xl text-white mt-1">{tiers[h] ? fmt(tiers[h]) : "—"}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-sm text-zinc-400 mt-5 leading-relaxed whitespace-pre-line">{listing.description}</p>
          <div className="mt-6">
            <BookingDialog type={type} listing={listing} testId={`detail-book-${listing.id}`} />
          </div>
        </div>

        <Dialog open={lightbox} onOpenChange={setLightbox}>
          <DialogContent className="bg-black/95 border-none max-w-6xl p-0">
            <DialogTitle className="sr-only">Expanded photo</DialogTitle>
            <button onClick={() => setLightbox(false)} data-testid="lightbox-close"
              className="absolute top-4 right-4 z-10 text-white/70 hover:text-white bg-black/50 rounded-full p-2" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <img src={images[active]} alt={listing.title} className="w-full max-h-[85vh] object-contain" />
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
